import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { buildApiUrl, getApiBaseUrl } from '../../config/apiConfig';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import FulfillmentTimeline from '../../components/FulfillmentTimeline/FulfillmentTimeline';
import { useStoreSettings } from '../../hooks/useStoreSettings';
import WhatsAppCoordinateButton from '../../components/WhatsAppCoordinateButton/WhatsAppCoordinateButton';
import { orderLinesFromPurchase } from '../../utils/whatsapp';
import './MyPurchasesPage.css';
import { logError } from '../../utils/logger';

const resolveImageUrl = (source) => {
  if (!source) return null;
  const sanitized = String(source).replace(/\\/g, '/').replace(/\s/g, '%20');
  if (/^https?:\/\//i.test(sanitized)) return sanitized;
  const base = getApiBaseUrl();
  if (sanitized.startsWith('/')) return `${base}${sanitized}`;
  if (sanitized.startsWith('uploads/')) return `${base}/${sanitized}`;
  return `${base}/uploads/${sanitized}`;
};

const getProductTypeLabel = (type) => {
  const typeMap = {
    course: 'Curso',
    event: 'Evento',
    item: 'Item',
    items: 'Item',
    physical: 'Item',
    asesoria: 'Asesoría',
    consult: 'Asesoría',
    custom: 'Producto',
  };
  return typeMap[type] || 'Producto';
};

const getFulfillmentStatusLabel = (status) => {
  if (!status) return 'Pendiente';
  return status;
};

const getFulfillmentStatusIcon = (status) => {
  if (!status) return 'fa-box';
  const statusLower = status.toLowerCase();
  if (statusLower.includes('despachado') || statusLower.includes('enviado')) return 'fa-truck';
  if (statusLower.includes('camino') || statusLower.includes('transito')) return 'fa-location-arrow';
  if (statusLower.includes('entregado') || statusLower.includes('recibido')) return 'fa-check-circle';
  if (statusLower.includes('tomando') || statusLower.includes('preparando')) return 'fa-box';
  return 'fa-box';
};

const getFulfillmentStatusColor = (status) => {
  if (!status) return { bg: '#d97706', color: '#ffffff' };
  const statusLower = status.toLowerCase();
  if (statusLower.includes('entregado') || statusLower.includes('recibido')) {
    return { bg: '#059669', color: '#ffffff' };
  }
  if (statusLower.includes('camino') || statusLower.includes('transito')) {
    return { bg: '#2563eb', color: '#ffffff' };
  }
  if (statusLower.includes('despachado') || statusLower.includes('enviado')) {
    return { bg: '#0891b2', color: '#ffffff' };
  }
  if (statusLower.includes('tomando') || statusLower.includes('preparando')) {
    return { bg: '#ea580c', color: '#ffffff' };
  }
  return { bg: '#64748b', color: '#ffffff' };
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('es-AR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatPrice = (price, currency = 'ARS') => {
  if (price === null || price === undefined || price === '') return '$0,00';
  const numPrice = parseFloat(price);
  if (Number.isNaN(numPrice)) return '$0,00';
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: currency || 'ARS',
  }).format(numPrice);
};

const lineImageSrc = (item) =>
  item.productImage || item.productDetails?.image || item.productDetails?.imagen || null;

const isPhysicalLine = (item) =>
  item.productType === 'item' || item.productType === 'items' || item.productType === 'physical';

/** Un solo estado de pedido por orden (no uno por línea). */
const fulfillmentRank = (status) => {
  const s = (status || '').toLowerCase();
  if (s.includes('tomando') || s.includes('preparando')) return 0;
  if (s.includes('despachado') || s.includes('enviado')) return 1;
  if (s.includes('camino') || s.includes('transito')) return 2;
  if (s.includes('entregado') || s.includes('recibido')) return 3;
  return 0;
};

const resolveOrderFulfillment = (order) => {
  const items = order?.items || [];
  const physical = items.filter((it) => isPhysicalLine(it));

  if (physical.length === 0) return null;

  const physicalWithStatus = physical.filter((it) => it.fulfillmentStatus);
  if (physicalWithStatus.length === 0) return null;

  const uniqueStatuses = [
    ...new Set(physicalWithStatus.map((it) => String(it.fulfillmentStatus).trim())),
  ];
  const status = uniqueStatuses.reduce((prev, curr) =>
    fulfillmentRank(prev) <= fulfillmentRank(curr) ? prev : curr,
  );

  return {
    status,
    shippingStatus: null,
    trackingNumber: null,
    events: [],
    hasMixedStatus: uniqueStatuses.length > 1,
    source: 'legacy',
  };
};

const FulfillmentBadge = ({ status, size = 'md' }) => {
  const c = getFulfillmentStatusColor(status);
  const sizeClass = size === 'lg' ? 'purchase-badge--lg' : '';
  return (
    <span
      className={`purchase-badge purchase-badge--ship ${sizeClass}`.trim()}
      style={{
        backgroundColor: c.bg,
        color: c.color,
        borderColor: 'transparent',
      }}
    >
      <i className={`fas ${getFulfillmentStatusIcon(status)}`} aria-hidden="true" />
      {getFulfillmentStatusLabel(status)}
    </span>
  );
};

const MyPurchasesPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { notify } = useNotifications();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const { settings } = useStoreSettings();

  useEffect(() => {
    if (!user || !token) {
      navigate('/login', { state: { from: '/my-purchases' } });
      return;
    }
    fetchOrders();
  }, [user, token, navigate]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await fetch(buildApiUrl('/api/orders'), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Error al cargar compras');
      }

      const data = await response.json();
      setOrders(data.orders || []);
    } catch (error) {
      logError('[MyPurchasesPage] Error fetching orders:', error);
      notify({ message: 'Error al cargar tus compras', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const toggleOrderExpansion = (orderId) => {
    setExpandedOrder(expandedOrder === orderId ? null : orderId);
  };

  const handleProductClick = (item) => {
    if (!item.productDetails) return;

    const { type, id } = item.productDetails;
    if (type === 'course') {
      navigate(`/courses/${id}`);
    } else if (type === 'event') {
      navigate(`/events/${id}`);
    } else if (type === 'item') {
      navigate(`/shop/${id}`);
    }
  };

  if (loading) {
    return (
      <div className="my-purchases-page">
        <div className="purchases-loading">
          <i className="fas fa-spinner fa-spin" aria-hidden="true" />
          <p>Cargando tus compras…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="my-purchases-page">
      <div className="purchases-container">
        <header className="purchases-header">
          <h1 className="purchases-title">
            <i className="fas fa-shopping-bag" aria-hidden="true" />
            Mis compras
          </h1>
          <p className="purchases-subtitle">Historial de tus pedidos y el detalle de cada producto</p>
        </header>

        {orders.length === 0 ? (
          <div className="purchases-empty">
            <i className="fas fa-shopping-bag" aria-hidden="true" />
            <h2>No tenés compras registradas</h2>
            <p>Cuando completes un pago, vas a verlo acá.</p>
            <button type="button" className="btn-browse" onClick={() => navigate('/courses')}>
              Explorar cursos
            </button>
          </div>
        ) : (
          <div className="purchases-stack">
            {orders.map((order) => {
              const items = order.items || [];
              const n = items.length;
              const preview = items.slice(0, 5);
              const expanded = expandedOrder === order.id;
              const orderFulfillment = resolveOrderFulfillment(order);

              return (
                <article key={order.id} className={`purchase-card${expanded ? ' purchase-card--expanded' : ''}`}>
                  <button
                    type="button"
                    className="purchase-card__header"
                    onClick={() => toggleOrderExpansion(order.id)}
                    aria-expanded={expanded}
                  >
                    <div className="purchase-card__lead">
                      <span className="purchase-card__order-id">{order.orderNumber}</span>
                      <time className="purchase-card__date" dateTime={order.created_at}>
                        {formatDate(order.created_at)}
                      </time>
                    </div>

                    <div className="purchase-card__preview" aria-hidden={n === 0}>
                      {n === 0 ? (
                        <span className="purchase-card__empty-line">Sin ítems</span>
                      ) : (
                        <>
                          <div className="purchase-thumb-stack">
                            {preview.map((item, idx) => {
                              const src = lineImageSrc(item);
                              return (
                                <div
                                  key={item.id ?? `${order.id}-p-${idx}`}
                                  className="purchase-thumb-stack__item"
                                  style={{ zIndex: preview.length - idx }}
                                >
                                  {src ? (
                                    <img
                                      src={resolveImageUrl(src)}
                                      alt=""
                                      className="purchase-thumb-stack__img"
                                      onError={(e) => {
                                        e.currentTarget.classList.add('purchase-thumb-stack__img--hidden');
                                      }}
                                    />
                                  ) : null}
                                  <div
                                    className={`purchase-thumb-stack__placeholder${!src ? ' purchase-thumb-stack__placeholder--show' : ''}`}
                                    aria-hidden="true"
                                  >
                                    <i className="fas fa-box" />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          <span className="purchase-card__count">
                            {n} {n === 1 ? 'producto' : 'productos'}
                          </span>
                        </>
                      )}
                    </div>

                    <div className="purchase-card__total">{formatPrice(order.totalAmount, order.currency)}</div>

                    <div className="purchase-card__status-block">
                      <span
                        className="purchase-badge purchase-badge--payment"
                        style={{ backgroundColor: order.statusColor }}
                      >
                        {order.statusLabel}
                      </span>
                      {orderFulfillment && (
                        <FulfillmentBadge status={orderFulfillment.status} />
                      )}
                    </div>

                    <i
                      className={`purchase-card__chevron fas fa-chevron-${expanded ? 'up' : 'down'}`}
                      aria-hidden="true"
                    />
                  </button>

                  {expanded && (
                    <div className="purchase-card__body" id={`purchase-detail-${order.id}`}>
                      <section className="purchase-detail-section">
                        <h2 className="purchase-detail-heading">Datos del pedido</h2>
                        <div className="purchase-info-grid">
                          <div className="purchase-info-cell">
                            <span className="purchase-info-label">Nº de orden</span>
                            <span className="purchase-info-value">{order.orderNumber}</span>
                          </div>
                          <div className="purchase-info-cell">
                            <span className="purchase-info-label">Fecha</span>
                            <span className="purchase-info-value">{formatDate(order.created_at)}</span>
                          </div>
                          <div className="purchase-info-cell">
                            <span className="purchase-info-label">Estado de pago</span>
                            <span
                              className="purchase-badge purchase-badge--payment purchase-badge--inline"
                              style={{ backgroundColor: order.statusColor }}
                            >
                              {order.statusLabel}
                            </span>
                          </div>
                          {orderFulfillment && (
                            <div className="purchase-info-cell purchase-info-cell--fulfillment purchase-info-cell--full">
                              <span className="purchase-info-label">Estado del pedido</span>
                              <div className="purchase-info-fulfillment">
                                <FulfillmentBadge status={orderFulfillment.status} size="lg" />
                                {orderFulfillment.hasMixedStatus && (
                                  <span className="purchase-info-hint">
                                    Algunos productos pueden tener avances distintos; mostramos el estado general del pedido.
                                  </span>
                                )}
                                <FulfillmentTimeline
                                  fulfillmentStatus={orderFulfillment.status}
                                />
                              </div>
                            </div>
                          )}
                          <div className="purchase-info-cell">
                            <span className="purchase-info-label">Total</span>
                            <span className="purchase-info-value purchase-info-value--accent">
                              {formatPrice(order.totalAmount, order.currency)}
                            </span>
                          </div>
                          {order.transaction?.transaction_id && (
                            <div className="purchase-info-cell">
                              <span className="purchase-info-label">ID de transacción</span>
                              <span className="purchase-info-value purchase-info-mono">
                                {order.transaction.transaction_id}
                              </span>
                            </div>
                          )}
                          {orderFulfillment && (
                            <div className="purchase-info-cell purchase-info-cell--full">
                              <WhatsAppCoordinateButton
                                phone={settings.whatsappPhone}
                                firstName={user?.nombre}
                                orderNumber={order.orderNumber}
                                totalAmount={order.totalAmount}
                                currency={order.currency}
                                lines={orderLinesFromPurchase(order)}
                              />
                            </div>
                          )}
                        </div>
                      </section>

                      <section className="purchase-detail-section">
                        <h2 className="purchase-detail-heading">
                          Productos
                          <span className="purchase-detail-count">{n}</span>
                        </h2>
                        {n === 0 ? (
                          <p className="purchase-no-items">No hay productos en esta orden.</p>
                        ) : (
                          <div className="purchase-items-grid">
                            {items.map((item, index) => {
                              const src = lineImageSrc(item);

                              return (
                                <article key={item.id ?? index} className="purchase-product-card">
                                  <div className="purchase-product-card__media">
                                    {src ? (
                                      <img
                                        src={resolveImageUrl(src)}
                                        alt=""
                                        className="purchase-product-card__img"
                                        onError={(e) => {
                                          e.currentTarget.classList.add('purchase-product-card__img--hidden');
                                        }}
                                      />
                                    ) : null}
                                    <div
                                      className={`purchase-product-card__ph${!src ? ' purchase-product-card__ph--show' : ''}`}
                                      aria-hidden="true"
                                    >
                                      <i className="fas fa-box" />
                                    </div>
                                  </div>
                                  <div className="purchase-product-card__body">
                                    <div className="purchase-product-card__head">
                                      <span className="purchase-line-type" data-type={item.productType}>
                                        {getProductTypeLabel(item.productType)}
                                      </span>
                                      <h3 className="purchase-product-card__name" title={item.productName}>
                                        {item.productName || 'Producto'}
                                      </h3>
                                    </div>
                                    <p className="purchase-product-card__pricing">
                                      <span className="purchase-product-card__qty">
                                        {item.quantity} × {formatPrice(item.unitPrice, order.currency)}
                                      </span>
                                      <strong className="purchase-product-card__subtotal">
                                        {formatPrice(item.totalPrice, order.currency)}
                                      </strong>
                                    </p>
                                    {item.productDetails && (
                                      <button
                                        type="button"
                                        className="purchase-product-card__link"
                                        onClick={() => handleProductClick(item)}
                                      >
                                        Ver producto
                                        <i className="fas fa-arrow-right" aria-hidden="true" />
                                      </button>
                                    )}
                                  </div>
                                </article>
                              );
                            })}
                          </div>
                        )}
                      </section>
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyPurchasesPage;
